import {
  sampleStatusColor,
  sampleStatusLabels,
  sampleTypeLabels,
} from "@/utils/labels";
import { panelCls } from "@/utils/panelCls";

interface SampleInfoProps {
  sample?: {
    id: string;
    name: string;
    sample_type: string;
    status: string;
    collection_location?: string;
    file_name?: string;
    file_size?: number;
    created_at?: string;
  };
  analysisStatus?: string;
  analysisType?: string;
  modelVersion?: string;
}

const SampleInfo = ({
  sample,
  analysisStatus,
  analysisType,
  modelVersion,
}: SampleInfoProps) => {
  // 선택된 샘플이 없는 경우
  if (!sample) {
    return (
      <div
        className={panelCls(
          "standard",
          "flex flex-col justify-center items-center h-full",
        )}
      >
        <div className="text-gray-500 text-sm">분석 결과를 선택해주세요</div>
      </div>
    );
  }

  return (
    <div className={panelCls("standard", "flex flex-col justify-start h-full")}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
        샘플 정보
      </h3>

      <div className="flex flex-col justify-start h-full gap-4 space-y-3">
        <div>
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            샘플 ID
          </div>
          <div className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded">
            {sample.id || "N/A"}
          </div>
        </div>
        <div>
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            샘플 이름
          </div>
          <div className="text-sm text-gray-900">{sample.name || "N/A"}</div>
        </div>
        <div>
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            샘플 유형
          </div>
          {/* 2026-08-28: 원값(gcms)이 그대로 나오고 있었다. 다른 화면(SampleDetailPage·
              CaseDetailPage)은 이미 labels.ts 맵을 쓴다 — 같은 값을 화면마다 다르게
              부르지 않는다. 맵에 없는 값은 원값을 그대로 둔다(지어내지 않는다). */}
          <div className="text-sm text-gray-900">
            {sample.sample_type
              ? (sampleTypeLabels[sample.sample_type] ?? sample.sample_type)
              : "N/A"}
          </div>
        </div>
        <div>
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            상태
          </div>
          {/* 2026-08-28: 여기서 비교하던 "completed"/"processing" 은 **백엔드에 없는
              값**이었다. 시료 상태 enum 은 registered/uploaded/analyzing/analyzed/failed
              라(labels.ts) 어떤 정상 시료도 두 분기에 걸리지 않고 else 로 떨어져
              **빨간 "오류" 배지**가 됐다 — 프로덕션 실측에서 시연 시료 DEMO-S-001
              (status="analyzed") 도 "오류"로 표시되고 있었다. 없는 사실을 말하는
              표시라 정정한다. 맵은 이미 있고 다른 두 화면이 쓰던 것이다. */}
          <div className="text-sm">
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${sampleStatusColor(
                sample.status,
              )}`}
            >
              {sampleStatusLabels[sample.status] ?? sample.status}
            </span>
          </div>
        </div>

        {/* 분석 정보 */}
        {(analysisStatus || analysisType || modelVersion) && (
          <div className="pt-2 border-t border-gray-200">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              분석 정보
            </div>
            <div className="space-y-2">
              {analysisType && (
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-600">분석 유형:</span>
                  <span className="font-mono text-gray-900">
                    {analysisType}
                  </span>
                </div>
              )}
              {analysisStatus && (
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-600">분석 상태:</span>
                  <span className="font-mono text-gray-900">
                    {analysisStatus}
                  </span>
                </div>
              )}
              {modelVersion && (
                <div className="flex justify-between items-center text-xs">
                  <span className="text-gray-600">모델 버전:</span>
                  <span className="font-mono text-gray-900">
                    {modelVersion}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 파일 정보 */}
        <div className="pt-2 border-t border-gray-200">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            파일 정보
          </div>
          <div className="space-y-2">
            {sample.file_name && (
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-600">파일명:</span>
                <span className="font-mono text-gray-900">
                  {sample.file_name}
                </span>
              </div>
            )}
            {sample.file_size && (
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-600">파일 크기:</span>
                <span className="text-gray-500">
                  ({(sample.file_size / 1024).toFixed(1)} KB)
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="pt-2 border-t border-gray-200">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            메모
          </div>
          <div className="text-xs text-gray-600 bg-yellow-50 p-2 rounded">
            {sample.collection_location
              ? `${sample.collection_location}에서 채취한 시료`
              : sample.created_at
                ? `등록일: ${sample.created_at}`
                : "추가 정보 없음"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SampleInfo;
